<template>
  <div class="internal-chat-list">
    <div class="internal-chat-list__header">
      <h1 class="internal-chat-list__title text-truncate">
        {{ $t('INTERNAL_CHAT.PAGE_TITLE') }}
      </h1>
      <woot-button size="small" icon="add" @click="$emit('new-chat')">
        {{ $t('INTERNAL_CHAT.NEW_CHAT') }}
      </woot-button>
    </div>

    <p v-if="isLoading" class="internal-chat-list__empty">
      {{ $t('INTERNAL_CHAT.LIST.LOADING') }}
    </p>
    <p v-else-if="!conversations.length" class="internal-chat-list__empty">
      {{ $t('INTERNAL_CHAT.LIST.EMPTY') }}
    </p>
    <ul v-else class="internal-chat-list__items">
      <li
        v-for="conversation in conversations"
        :key="conversation.id"
        class="internal-chat-list__item"
        :class="{ 'is-active': isActive(conversation) }"
        @click="$emit('select', conversation)"
      >
        <woot-thumbnail
          :src="conversation.other_user.avatar_url"
          :username="conversation.other_user.name"
          size="36px"
        />
        <div class="internal-chat-list__item-content">
          <div class="internal-chat-list__item-top">
            <span class="internal-chat-list__item-name text-truncate">
              {{ conversation.other_user.name }}
            </span>
            <span class="internal-chat-list__item-time">
              {{ formattedTime(conversation.last_activity_at) }}
            </span>
          </div>
          <div class="internal-chat-list__item-bottom">
            <span
              v-if="conversation.readonly"
              class="internal-chat-list__item-readonly"
            >
              {{ $t('INTERNAL_CHAT.LIST.READONLY_TAG') }}
            </span>
            <span v-else class="internal-chat-list__item-spacer" />
            <span
              v-if="conversation.unread_count"
              class="internal-chat-list__unread-badge"
            >
              {{ conversation.unread_count }}
            </span>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { formatDistanceToNow } from 'date-fns';

export default {
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    activeConversationId: {
      type: [String, Number],
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isActive(conversation) {
      return Number(conversation.id) === Number(this.activeConversationId);
    },
    formattedTime(timestamp) {
      if (!timestamp) {
        return '';
      }
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    },
  },
};
</script>

<style scoped lang="scss">
.internal-chat-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 1px solid var(--color-border);
}

.internal-chat-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-normal);
  border-bottom: 1px solid var(--color-border);
}

.internal-chat-list__title {
  font-size: var(--font-size-big);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.internal-chat-list__empty {
  padding: var(--space-normal);
  color: var(--s-500);
}

.internal-chat-list__items {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
}

.internal-chat-list__item {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  cursor: pointer;

  &:hover {
    background: var(--color-background-light);
  }

  &.is-active {
    background: var(--w-50);
  }
}

.internal-chat-list__item-content {
  flex: 1;
  min-width: 0;
}

.internal-chat-list__item-top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-smaller);
}

.internal-chat-list__item-name {
  font-weight: var(--font-weight-medium);
}

.internal-chat-list__item-time {
  font-size: var(--font-size-mini);
  color: var(--s-500);
  white-space: nowrap;
}

.internal-chat-list__item-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-micro);
}

.internal-chat-list__item-readonly {
  font-size: var(--font-size-mini);
  color: var(--y-700);
}

.internal-chat-list__item-spacer {
  flex: 1;
}

.internal-chat-list__unread-badge {
  background: var(--w-500);
  color: var(--white);
  border-radius: var(--border-radius-rounded);
  font-size: var(--font-size-mini);
  min-width: 1.8rem;
  height: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-smaller);
}
</style>
